import { z } from "zod";
import { databaseProviderSchema, databaseRelationSchema } from "./database-connections.js";
import { connectorProviderSchema } from "./connectors.js";
import { organizationMemberSchema } from "./organization-members.js";
export const dataAccessPrincipalSchema = z.object({
    kind: z.literal("human"),
    id: z.string().trim().min(1).max(300),
}).strict();
/**
 * Sources that carry access entities: every connector provider, plus
 * third-party Postgres connections, whose id stands in for the integration id
 * and whose entities are `schema.relation` names.
 */
export const dataAccessSourceProviderSchema = z.union([
    z.literal("asana"), // Live resource grants; ingestion is registered separately.
    connectorProviderSchema,
    z.literal("postgres"),
    databaseProviderSchema,
]);
/**
 * The smallest customer-visible access unit. `entity` is the projected
 * relation entity (for example `invoices` or `invoice_lines`), not a provider
 * API operation; for Postgres connections it is the `schema.relation` name.
 * Read/query is the only implied operation.
 */
export const connectorEntityRefSchema = z.object({
    connectorIntegrationId: z.string().trim().min(1).max(300),
    provider: dataAccessSourceProviderSchema,
    entity: z.string().trim().max(400),
}).strict().refine(value => value.provider === "azure-sql" || value.provider === "bigquery"
    ? databaseRelationSchema.safeParse(value.entity).success && value.entity.split(".").length === (value.provider === "bigquery" ? 3 : 2)
    : value.entity.length <= 160 && /^[a-z][a-z0-9_]*(\.[a-z_][a-z0-9_$]*)?$/u.test(value.entity), { message: "Use a valid entity name for this source", path: ["entity"] });
export const dataAccessRevisionVectorSchema = z.record(z.string().trim().min(1).max(160), z.number().int().nonnegative());
export const dataAccessDecisionRequestV1Schema = z.object({
    version: z.literal(1).default(1),
    principal: dataAccessPrincipalSchema,
    entity: connectorEntityRefSchema,
}).strict();
export const dataAccessDecisionResponseV1Schema = z.object({
    version: z.literal(1).default(1),
    decisionId: z.string().trim().min(1).max(200),
    outcome: z.enum(["allow", "deny"]),
    reasonCode: z.string().trim().min(1).max(160),
    policyRevisionVector: dataAccessRevisionVectorSchema,
    retryable: z.boolean().default(false),
}).strict();
export const dataRoleSchema = z.object({
    id: z.string().trim().min(1).max(300),
    organisationId: z.string().trim().min(1).max(300),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).optional(),
    status: z.enum(["active", "disabled"]),
    entities: z.array(connectorEntityRefSchema),
    memberPrincipalIds: z.array(z.string().trim().min(1).max(300)),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
}).strict();
export const createDataRoleInputSchema = z.object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).optional(),
    entities: z.array(connectorEntityRefSchema).max(500).default([]),
    memberPrincipalIds: z.array(z.string().trim().min(1).max(300)).max(500).default([]),
}).strict();
export const updateDataRoleInputSchema = z.object({
    expectedUpdatedAt: z.number().int().nonnegative(),
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    status: z.enum(["active", "disabled"]).optional(),
    entities: z.array(connectorEntityRefSchema).max(500).optional(),
    memberPrincipalIds: z.array(z.string().trim().min(1).max(300)).max(500).optional(),
}).strict().refine((value) => Object.keys(value).some((key) => key !== "expectedUpdatedAt"), {
    message: "At least one data-role field must be updated",
});
export const dataAccessExplanationSchema = z.object({
    decision: dataAccessDecisionResponseV1Schema,
    entity: connectorEntityRefSchema,
    organisationAdmin: z.boolean(),
    matchedDataRoleIds: z.array(z.string()),
}).strict();
export const dataAccessQueryInputSchema = z.object({
    principal: dataAccessPrincipalSchema.optional(),
    entity: connectorEntityRefSchema,
}).strict();
export const effectiveDataAccessInputSchema = z.object({
    requests: z.array(dataAccessQueryInputSchema).min(1).max(100),
}).strict();
export const effectiveDataAccessResponseSchema = z.object({
    allowed: z.array(dataAccessExplanationSchema),
}).strict();
export const connectorEntityCatalogueItemSchema = connectorEntityRefSchema.safeExtend({
    sourceEntity: z.string().trim().min(1).max(160),
    relationSchema: z.string().trim().min(1).max(256),
    relationName: z.string().trim().min(1).max(160),
    contractHash: z.string().regex(/^[a-f0-9]{64}$/u),
    enabled: z.boolean(),
    /** Human-readable row grain from the governed projection contract. */
    grain: z.string().trim().min(1).max(500).optional(),
    /** Empty connector ownership is organisation-scoped; a shared mailbox owns itself; otherwise personal. */
    connectionScope: z.enum(["organisation", "user", "mailbox"]).optional(),
    ownerUserId: z.string().trim().min(1).max(300).optional(),
    /** The address of a shared mailbox, so Access can name it. */
    mailboxAddress: z.string().trim().email().max(320).optional(),
}).strict();
export const listDataRolesResponseSchema = z.object({
    roles: z.array(dataRoleSchema),
}).strict();
export const listConnectorEntitiesResponseSchema = z.object({
    entities: z.array(connectorEntityCatalogueItemSchema),
}).strict();
export const listDataRoleMembersResponseSchema = z.object({
    members: z.array(organizationMemberSchema),
    directory: z.array(organizationMemberSchema),
}).strict();
